/**
 * RC-WEB-5 GATE 16 — the authenticated customer app namespace (`app.*`).
 *
 * Every string rendered by the authenticated surfaces (shell, inbox, calls,
 * trusted contacts, dashboard, membership and checkout) lives here in the four
 * supported locales. No authenticated page may hardcode Spanish any more.
 */

const es = {
  back: "Volver",
  save: "Guardar",
  loading: "Cargando…",
  retry: "Reintentar",
  nav: {
    overview: "Resumen",
    overview_mobile: "Inicio",
    protections: "Protecciones",
    messages: "Mensajes",
    calls: "Llamadas",
    trusted: "Contactos de confianza",
    sticker: "Sticker Studio",
    membership: "Membresía y pagos",
    settings: "Ajustes",
    logout: "Cerrar sesión",
    main: "Navegación principal",
    bottom: "Navegación inferior",
    open_menu: "Abrir menú",
    close_menu: "Cerrar menú",
    logged_out: "Sesión cerrada",
    tagline: "Contacto privado para lo que quieres proteger.",
  },
  inbox: {
    title: "Mensajes",
    unread_n: "{n} mensajes sin leer",
    unread_1: "1 mensaje sin leer",
    all_clear: "Todo al día",
    search: "Buscar por protección o texto",
    filter_all: "Todas",
    filter_unread: "No leídas",
    filter_urgent: "Urgentes",
    empty_title: "Todavía no hay mensajes",
    empty_text: "Cuando alguien escanee tu QR y te escriba, la conversación aparecerá aquí. Tú y tus contactos de confianza la verán al instante.",
    empty_filter_title: "Ningún mensaje con ese filtro",
    empty_filter_text: "Prueba con otro filtro o limpia la búsqueda.",
    empty_search_title: "Sin resultados",
    empty_search_text: "No encontramos conversaciones que coincidan con «{query}». Prueba con otras palabras o limpia la búsqueda.",
    view_qr: "Ver mis QR",
    select_title: "Selecciona una conversación",
    select_text: "Elige un mensaje de la lista para leerlo y responder.",
    select_privacy: "Las respuestas viajan dentro de AvisoCar: quien escanea nunca ve tu teléfono ni tu correo.",
    historic: "Histórico",
    trusted_badge: "Contacto de confianza",
    no_messages: "Sin mensajes",
    protection: "Protección",
    open_state: "Conversación abierta",
    closed_state: "Conversación cerrada",
    urgent: "Aviso urgente",
    block: "Bloquear",
    report: "Reportar",
    reply_placeholder: "Escribe tu respuesta",
    readonly_legacy: "Mensajes históricos: esta conversación es solo de lectura.",
    readonly_closed: "Esta conversación está cerrada.",
    not_available: "Esta conversación ya no está disponible.",
    open_error: "No pudimos abrir esta conversación",
    send_error: "No se pudo enviar. Inténtalo de nuevo",
    send_blocked: "Ese mensaje no se puede enviar",
    blocked_ok: "Conversación bloqueada",
    reported_ok: "Reporte enviado al equipo de AvisoCar",
    closed_ok: "Conversación cerrada",
    action_error: "No se pudo completar la acción",
    now: "ahora",
    min_ago: "hace {n} min",
    hour_ago: "hace {n} h",
    day_ago: "hace {n} d",
  },
  protection: {
    block: "Bloquear conversación",
    unblock: "Desbloquear conversación",
    close: "Cerrar conversación",
    report: "Reportar abuso",
    reason_label: "Motivo",
    details_label: "Detalle (opcional)",
    details_ph: "Cuéntanos qué pasó. No copiamos el contenido de los mensajes.",
    cancel: "Cancelar",
    confirm_block: "Bloquear conversación",
    confirm_report: "Enviar reporte",
    reason: {
      harassment: "Acoso o insistencia molesta",
      threats: "Amenazas",
      spam: "Spam o publicidad",
      inappropriate: "Contenido inapropiado",
      not_relevant: "No tiene que ver conmigo",
      other: "Otro motivo",
    },
    block_explain: "Se bloquea SOLO esta conversación. Quien la inició no podrá seguir escribiéndote aquí.",
    report_explain: "El reporte llega al equipo de AvisoCar con la referencia de esta conversación y tu motivo. No copiamos el texto de los mensajes.",
    scope_note: "Tu QR sigue activo, tus contactos de confianza siguen recibiendo avisos y cualquier otra persona que escanee puede escribirte o llamarte con normalidad.",
    blocked_badge: "Conversación bloqueada",
    closed_badge: "Conversación cerrada",
    readonly_blocked: "Bloqueaste esta conversación. Puedes desbloquearla cuando quieras.",
    blocked_ok: "Conversación bloqueada",
    unblocked_ok: "Conversación desbloqueada",
    reported_ok: "Reporte enviado al equipo de AvisoCar",
    closed_ok: "Conversación cerrada",
    error: "No se pudo completar la acción",
    blocked_title: "Conversaciones bloqueadas",
    blocked_desc: "Aquí ves las conversaciones que bloqueaste o cerraste. El bloqueo es por conversación, no por persona: quien escanea tu QR es anónimo y no se puede identificar entre dispositivos.",
    blocked_empty: "No has bloqueado ninguna conversación.",
    open_conversation: "Ver",
    rotate_sticker_warning: "Al rotar el QR, tu sticker YA IMPRESO deja de funcionar. Tendrás que descargar e imprimir el nuevo código desde Sticker Studio.",
    rotate_sticker_done: "QR rotado. Tu sticker impreso ya no funciona: descarga e imprime el nuevo código.",
  },
  calls: {
    title: "Llamadas",
    subtitle: "Historial de llamadas privadas por AvisoCar",
    empty_title: "Aún no hay llamadas",
    empty_text: "Quien escanee tu QR puede pedir una llamada privada por internet. Sonará para ti y para tus contactos de confianza con permiso de llamadas; quien conteste primero se queda con la llamada.",
    manage_trusted: "Gestionar contactos de confianza",
    answered_by: "Atendió",
    view: "Ver",
    state: {
      answered: "Atendida",
      ringing: "Sonando",
      pending: "Solicitada",
      missed: "Perdida",
      declined: "Rechazada",
      cancelled: "Cancelada",
      ended: "Finalizada",
      requested: "Solicitada",
    },
    who: { owner: "Dueño", trusted: "Contacto de confianza", you: "Tú" },
  },
  trusted: {
    title: "Contactos de confianza",
    subtitle: "¿Quién puede ayudarte cuando no puedas responder?",
    intro: "Invita a una persona de confianza para que también reciba los mensajes y llamadas de tus QR. Tú decides qué protecciones puede atender y qué permisos tendrá.",
    step_l1: "INVITAR",
    step_l2: "ACEPTAR",
    step_l3: "ASIGNAR PROTECCIÓN",
    step_l4: "ELEGIR PERMISOS",
    step_l5: "RECIBIR AVISOS",
    how_title: "Cómo funciona",
    step1: "Invita a alguien de tu confianza por correo.",
    step2: "Esa persona acepta con su propia cuenta de AvisoCar.",
    step3: "Le asignas uno o más perfiles protegidos.",
    step4: "Eliges si recibe mensajes, llamadas o ambos.",
    step5: "Puede responder sin compartir números de teléfono.",
    invite_title: "Invitar a una persona",
    email_label: "Correo de la persona",
    name_label: "Nombre privado (solo tú lo ves)",
    label_label: "Etiqueta pública (opcional)",
    label_hint: "Es lo único que verá quien escanee. Si la dejas vacía se mostrará “Contacto de confianza”.",
    submit: "Enviar invitación",
    invite_created: "Invitación creada. Comparte el enlace con esa persona.",
    invite_error: "No pudimos crear la invitación",
    invite_self: "No puedes invitarte a ti mismo",
    invite_link: "Enlace de invitación (válido 14 días):",
    list_title: "Tus contactos",
    show_revoked: "Ver revocados ({n})",
    hide_revoked: "Ocultar revocados",
    empty_title: "Todavía no invitaste a nadie",
    empty_text: "Un contacto de confianza puede leer los mensajes y atender las llamadas de los perfiles que le asignes, sin ver tu teléfono ni tu correo.",
    empty_none_active: "No tienes contactos activos ni invitaciones pendientes.",
    load_error: "No pudimos cargar tus contactos de confianza",
    status: {
      invited: "Invitación enviada",
      accepted: "Aceptado · sin perfiles",
      assigned: "Activo",
      declined: "Rechazada",
      revoked: "Revocado",
      expired: "Invitación vencida",
    },
    status_hint: {
      invited: "Esperando que acepte con su cuenta de AvisoCar. Todavía no recibe avisos.",
      accepted: "Ya aceptó, pero aún no recibe avisos: asígnale al menos un perfil protegido.",
      assigned: "Recibe los avisos de los perfiles asignados según sus permisos.",
      declined: "Esta persona rechazó la invitación.",
      revoked: "Ya no recibe ningún aviso de tus QR.",
      expired: "El enlace caducó. Envía una invitación nueva.",
    },
    perm_messages: "Recibir mensajes",
    perm_calls: "Recibir llamadas",
    perm_none: "Sin permisos activos: no recibirá avisos aunque tenga perfiles asignados.",
    assigned_profiles: "Perfiles protegidos asignados",
    assigned_count: "{n} perfiles asignados",
    assigned_none: "Ningún perfil asignado",
    no_profiles: "Aún no tienes perfiles protegidos.",
    public_label: "Etiqueta pública",
    public_label_saved: "Etiqueta pública guardada",
    public_label_error: "Etiqueta no válida (no puede ser un teléfono, email ni enlace)",
    perm_error: "No pudimos guardar el permiso",
    profiles_saved: "Perfiles asignados actualizados",
    profiles_error: "No pudimos actualizar los perfiles asignados",
    revoke: "Revocar",
    revoke_confirm: "¿Revocar este contacto de confianza? Dejará de recibir avisos.",
    revoked_ok: "Contacto revocado",
    revoke_error: "No pudimos revocar el contacto",
    updated: "Actualizado",
    invited_at: "Invitado",
    accepted_at: "Aceptado",
  },
  dash: {
    greeting: "Hola, {name}",
    active_n: "{n} protecciones activas",
    active_1: "1 protección activa",
    activate_first: "Activa tu primera protección",
    refresh: "Actualizar",
    refreshed: "Panel actualizado",
    stat_qr: "QR activos",
    stat_unread: "Mensajes sin leer",
    stat_missed: "Llamadas perdidas",
    stat_trusted: "Contactos activos",
    my_protections: "Mis protecciones",
    new: "Nueva",
    empty_title: "Aún no tienes protecciones",
    empty_text: "Crea una protección para tu vehículo, tu mascota, un objeto o un menor y recibe avisos privados cuando alguien escanee su QR.",
    create_first: "Crear mi primera protección",
    membership: "Membresía",
    no_membership: "Sin membresía activa",
    plan_months: "{n} meses",
    payment_review: "Pago en revisión",
    expires_on: "Vence el {date}",
    activate_qr: "Actívala para encender tu QR",
    recent: "Actividad reciente",
    see_all: "Ver todo",
    recent_empty: "Cuando alguien escanee tu QR verás aquí sus mensajes y llamadas.",
    trusted_ready: "Contactos de confianza",
    trusted_none: "Nadie más recibe tus avisos todavía.",
    trusted_pending: "{n} invitación sin aceptar todavía.",
    trusted_unassigned: "{n} contacto aceptado sin perfiles asignados.",
    trusted_ok: "{n} contacto recibiendo tus avisos.",
    trusted_manage: "Gestionar",
    view: "Ver",
    sticker: "Sticker",
    pay: "Pagar",
    status: {
      active: "Activo",
      pending_payment: "Pago pendiente",
      rejected: "Rechazado",
      review: "En revisión",
      pending: "Pendiente",
    },
  },
  membership: {
    title: "Membresía y pagos",
    subtitle: "Tu protección, tu plan y cómo pagarlo",
    hero_title: "Protección activa para lo que te importa",
    hero_text: "Tu membresía mantiene encendidos los QR de tus protecciones: mensajes privados, llamadas por internet y avisos a tus contactos de confianza.",
    status: "Estado",
    protected_count: "Protecciones activas",
    expires: "Vence",
    days_left: "Días restantes",
    days_value: "{n} días",
    status_active: "Activa",
    status_expired: "Vencida",
    status_standby: "En espera",
    status_disabled: "Desactivada",
    status_pending: "Pago pendiente",
    grace: "(en gracia)",
    pending_request: "Tienes una solicitud de pago en revisión.",
    current_plan: "Tu plan actual",
    choose_plan: "Elige tu plan",
    recommended: "Recomendado",
    selected: "Seleccionado",
    select: "Elegir",
    plan_6m: "6 meses",
    plan_12m: "12 meses",
    per_months: "por {n} meses de protección",
    benefits_title: "Incluido en tu membresía",
    benefit_qr: "Contacto privado por QR, sin mostrar tu teléfono",
    benefit_messages: "Mensajes con quien escanea, dentro de AvisoCar",
    benefit_calls: "Llamadas privadas por internet",
    benefit_trusted: "Avisos a tus contactos de confianza",
    benefit_sticker: "Descarga de QR y stickers en PNG y PDF",
    benefit_manage: "Gestión de todas tus protecciones desde un panel",
    summary_title: "Resumen de tu pedido",
    summary_plan: "Plan elegido",
    summary_amount: "Total a pagar",
    summary_method: "Forma de pago",
    summary_after: "Después de pagar",
    summary_after_text: "Revisamos tu pago y activamos tus QR. Te avisamos dentro de AvisoCar en cuanto quede activo.",
    charge_note: "Cobro real: {pen} en soles (PEN).",
    estimate_note: "Importe aproximado en tu moneda. El cobro real se hace en soles (PEN).",
    coupon_title: "¿Tienes un código de descuento?",
    pay_title: "Cómo quieres pagar",
    pay_none: "Ahora mismo no hay formas de pago disponibles para tu país. Escríbenos y lo resolvemos.",
    history: "Historial de pagos",
  },
  settings: {
    title: "Ajustes",
    subtitle: "Tu cuenta, tus avisos y tu privacidad",
  },
  legal: {
    doc_terms: "Términos y Condiciones",
    doc_privacy: "Política de Privacidad",
    doc_complaints: "Libro de Reclamaciones (Perú)",
    banner_region: "Actualización legal pendiente",
    banner_text: "Actualizamos {docs}. Revísalo y confirma para seguir usando AvisoCar con normalidad.",
    review: "Revisar",
    dismiss: "Cerrar aviso legal",
    close: "Cerrar",
    accept: "Acepto",
    later: "Más tarde",
    accepted_ok: "Gracias, quedó registrado",
    accept_error: "No pudimos registrar tu aceptación",
    load_error: "No pudimos cargar el documento.",
  },
};

const en = {
  back: "Back",
  save: "Save",
  loading: "Loading…",
  retry: "Try again",
  nav: {
    overview: "Overview",
    overview_mobile: "Home",
    protections: "Protections",
    messages: "Messages",
    calls: "Calls",
    trusted: "Trusted contacts",
    sticker: "Sticker Studio",
    membership: "Membership & payments",
    settings: "Settings",
    logout: "Log out",
    main: "Main navigation",
    bottom: "Bottom navigation",
    open_menu: "Open menu",
    close_menu: "Close menu",
    logged_out: "Signed out",
    tagline: "Private contact for whatever you want to protect.",
  },
  inbox: {
    title: "Messages",
    unread_n: "{n} unread messages",
    unread_1: "1 unread message",
    all_clear: "All caught up",
    search: "Search by protection or text",
    filter_all: "All",
    filter_unread: "Unread",
    filter_urgent: "Urgent",
    empty_title: "No messages yet",
    empty_text: "When someone scans your QR and writes to you, the conversation shows up here. You and your trusted contacts see it instantly.",
    empty_filter_title: "No messages match this filter",
    empty_filter_text: "Try another filter or clear the search.",
    empty_search_title: "No results",
    empty_search_text: "We found no conversations matching “{query}”. Try other words or clear the search.",
    view_qr: "View my QR codes",
    select_title: "Select a conversation",
    select_text: "Pick a message from the list to read it and reply.",
    select_privacy: "Replies stay inside AvisoCar: whoever scans never sees your phone number or email.",
    historic: "Archived",
    trusted_badge: "Trusted contact",
    no_messages: "No messages",
    protection: "Protection",
    open_state: "Conversation open",
    closed_state: "Conversation closed",
    urgent: "Urgent alert",
    block: "Block",
    report: "Report",
    reply_placeholder: "Write your reply",
    readonly_legacy: "Archived messages: this conversation is read-only.",
    readonly_closed: "This conversation is closed.",
    not_available: "This conversation is no longer available.",
    open_error: "We couldn't open this conversation",
    send_error: "Couldn't send. Please try again",
    send_blocked: "That message can't be sent",
    blocked_ok: "Conversation blocked",
    reported_ok: "Report sent to the AvisoCar team",
    closed_ok: "Conversation closed",
    action_error: "We couldn't complete that action",
    now: "now",
    min_ago: "{n} min ago",
    hour_ago: "{n} h ago",
    day_ago: "{n} d ago",
  },
  protection: {
    block: "Block conversation",
    unblock: "Unblock conversation",
    close: "Close conversation",
    report: "Report abuse",
    reason_label: "Reason",
    details_label: "Details (optional)",
    details_ph: "Tell us what happened. We never copy the message content.",
    cancel: "Cancel",
    confirm_block: "Block conversation",
    confirm_report: "Send report",
    reason: {
      harassment: "Harassment or repeated pestering",
      threats: "Threats",
      spam: "Spam or advertising",
      inappropriate: "Inappropriate content",
      not_relevant: "Not related to me",
      other: "Other reason",
    },
    block_explain: "This blocks ONLY this conversation. Whoever started it will not be able to keep writing to you here.",
    report_explain: "The report reaches the AvisoCar team with this conversation's reference and your reason. We never copy the message text.",
    scope_note: "Your QR stays active, your trusted contacts keep receiving notices, and anybody else who scans can still write or call you normally.",
    blocked_badge: "Conversation blocked",
    closed_badge: "Conversation closed",
    readonly_blocked: "You blocked this conversation. You can unblock it whenever you want.",
    blocked_ok: "Conversation blocked",
    unblocked_ok: "Conversation unblocked",
    reported_ok: "Report sent to the AvisoCar team",
    closed_ok: "Conversation closed",
    error: "We could not complete the action",
    blocked_title: "Blocked conversations",
    blocked_desc: "Here are the conversations you blocked or closed. Blocking is per conversation, not per person: whoever scans your QR is anonymous and cannot be identified across devices.",
    blocked_empty: "You have not blocked any conversation.",
    open_conversation: "View",
    rotate_sticker_warning: "Rotating the QR stops your ALREADY PRINTED sticker from working. You will need to download and print the new code from Sticker Studio.",
    rotate_sticker_done: "QR rotated. Your printed sticker no longer works: download and print the new code.",
  },
  calls: {
    title: "Calls",
    subtitle: "History of private AvisoCar calls",
    empty_title: "No calls yet",
    empty_text: "Whoever scans your QR can request a private internet call. It rings for you and for your trusted contacts with call permission; whoever answers first takes the call.",
    manage_trusted: "Manage trusted contacts",
    answered_by: "Answered by",
    view: "View",
    state: {
      answered: "Answered",
      ringing: "Ringing",
      pending: "Requested",
      missed: "Missed",
      declined: "Declined",
      cancelled: "Cancelled",
      ended: "Ended",
      requested: "Requested",
    },
    who: { owner: "Owner", trusted: "Trusted contact", you: "You" },
  },
  trusted: {
    title: "Trusted contacts",
    subtitle: "Who can help you when you can't answer?",
    intro: "Invite someone you trust so they also receive the messages and calls from your QR codes. You decide which protections they can handle and what permissions they get.",
    step_l1: "INVITE",
    step_l2: "ACCEPT",
    step_l3: "ASSIGN PROTECTION",
    step_l4: "CHOOSE PERMISSIONS",
    step_l5: "RECEIVE ALERTS",
    how_title: "How it works",
    step1: "Invite someone you trust by email.",
    step2: "They accept using their own AvisoCar account.",
    step3: "You assign them one or more protected profiles.",
    step4: "You choose whether they receive messages, calls or both.",
    step5: "They can reply without sharing phone numbers.",
    invite_title: "Invite someone",
    email_label: "Their email",
    name_label: "Private name (only you see it)",
    label_label: "Public label (optional)",
    label_hint: "This is the only thing the person scanning sees. Leave it empty to show “Trusted contact”.",
    submit: "Send invitation",
    invite_created: "Invitation created. Share the link with that person.",
    invite_error: "We couldn't create the invitation",
    invite_self: "You can't invite yourself",
    invite_link: "Invitation link (valid for 14 days):",
    list_title: "Your contacts",
    show_revoked: "Show revoked ({n})",
    hide_revoked: "Hide revoked",
    empty_title: "You haven't invited anyone yet",
    empty_text: "A trusted contact can read the messages and take the calls of the profiles you assign, without seeing your phone number or email.",
    empty_none_active: "You have no active contacts or pending invitations.",
    load_error: "We couldn't load your trusted contacts",
    status: {
      invited: "Invitation sent",
      accepted: "Accepted · no profiles",
      assigned: "Active",
      declined: "Declined",
      revoked: "Revoked",
      expired: "Invitation expired",
    },
    status_hint: {
      invited: "Waiting for them to accept with their AvisoCar account. They receive no alerts yet.",
      accepted: "They accepted, but receive no alerts yet: assign at least one protected profile.",
      assigned: "They receive the alerts of the assigned profiles according to their permissions.",
      declined: "This person declined the invitation.",
      revoked: "They no longer receive any alert from your QR codes.",
      expired: "The link expired. Send a new invitation.",
    },
    perm_messages: "Receive messages",
    perm_calls: "Receive calls",
    perm_none: "No active permissions: they won't receive alerts even with assigned profiles.",
    assigned_profiles: "Assigned protected profiles",
    assigned_count: "{n} assigned profiles",
    assigned_none: "No profile assigned",
    no_profiles: "You don't have protected profiles yet.",
    public_label: "Public label",
    public_label_saved: "Public label saved",
    public_label_error: "Invalid label (it can't be a phone number, email or link)",
    perm_error: "We couldn't save that permission",
    profiles_saved: "Assigned profiles updated",
    profiles_error: "We couldn't update the assigned profiles",
    revoke: "Revoke",
    revoke_confirm: "Revoke this trusted contact? They will stop receiving alerts.",
    revoked_ok: "Contact revoked",
    revoke_error: "We couldn't revoke the contact",
    updated: "Updated",
    invited_at: "Invited",
    accepted_at: "Accepted",
  },
  dash: {
    greeting: "Hi, {name}",
    active_n: "{n} active protections",
    active_1: "1 active protection",
    activate_first: "Activate your first protection",
    refresh: "Refresh",
    refreshed: "Dashboard updated",
    stat_qr: "Active QR codes",
    stat_unread: "Unread messages",
    stat_missed: "Missed calls",
    stat_trusted: "Active contacts",
    my_protections: "My protections",
    new: "New",
    empty_title: "You don't have protections yet",
    empty_text: "Create a protection for your vehicle, your pet, an object or a child and get private alerts when someone scans its QR.",
    create_first: "Create my first protection",
    membership: "Membership",
    no_membership: "No active membership",
    plan_months: "{n} months",
    payment_review: "Payment under review",
    expires_on: "Expires on {date}",
    activate_qr: "Activate it to switch on your QR",
    recent: "Recent activity",
    see_all: "See all",
    recent_empty: "When someone scans your QR you'll see their messages and calls here.",
    trusted_ready: "Trusted contacts",
    trusted_none: "Nobody else receives your alerts yet.",
    trusted_pending: "{n} invitation not accepted yet.",
    trusted_unassigned: "{n} accepted contact without assigned profiles.",
    trusted_ok: "{n} contact receiving your alerts.",
    trusted_manage: "Manage",
    view: "View",
    sticker: "Sticker",
    pay: "Pay",
    status: {
      active: "Active",
      pending_payment: "Payment pending",
      rejected: "Rejected",
      review: "Under review",
      pending: "Pending",
    },
  },
  membership: {
    title: "Membership & payments",
    subtitle: "Your protection, your plan and how to pay for it",
    hero_title: "Active protection for what matters to you",
    hero_text: "Your membership keeps the QR codes of your protections switched on: private messages, internet calls and alerts to your trusted contacts.",
    status: "Status",
    protected_count: "Active protections",
    expires: "Expires",
    days_left: "Days left",
    days_value: "{n} days",
    status_active: "Active",
    status_expired: "Expired",
    status_standby: "On hold",
    status_disabled: "Disabled",
    status_pending: "Payment pending",
    grace: "(in grace period)",
    pending_request: "You have a payment request under review.",
    current_plan: "Your current plan",
    choose_plan: "Choose your plan",
    recommended: "Recommended",
    selected: "Selected",
    select: "Choose",
    plan_6m: "6 months",
    plan_12m: "12 months",
    per_months: "for {n} months of protection",
    benefits_title: "Included in your membership",
    benefit_qr: "Private QR contact without showing your phone number",
    benefit_messages: "Messages with whoever scans, inside AvisoCar",
    benefit_calls: "Private internet calls",
    benefit_trusted: "Alerts to your trusted contacts",
    benefit_sticker: "QR and sticker downloads in PNG and PDF",
    benefit_manage: "Manage all your protections from one dashboard",
    summary_title: "Order summary",
    summary_plan: "Chosen plan",
    summary_amount: "Total to pay",
    summary_method: "Payment method",
    summary_after: "After you pay",
    summary_after_text: "We review your payment and switch on your QR codes. We notify you inside AvisoCar as soon as it is active.",
    charge_note: "Actual charge: {pen} in Peruvian soles (PEN).",
    estimate_note: "Approximate amount in your currency. The actual charge is made in Peruvian soles (PEN).",
    coupon_title: "Have a discount code?",
    pay_title: "How do you want to pay",
    pay_none: "There are no payment methods available for your country right now. Write to us and we'll sort it out.",
    history: "Payment history",
  },
  settings: {
    title: "Settings",
    subtitle: "Your account, your alerts and your privacy",
  },
  legal: {
    doc_terms: "Terms and Conditions",
    doc_privacy: "Privacy Policy",
    doc_complaints: "Complaints Book (Peru)",
    banner_region: "Legal update pending",
    banner_text: "We updated {docs}. Review it and confirm to keep using AvisoCar normally.",
    review: "Review",
    dismiss: "Dismiss legal notice",
    close: "Close",
    accept: "I accept",
    later: "Later",
    accepted_ok: "Thanks, it's on record",
    accept_error: "We couldn't record your acceptance",
    load_error: "We couldn't load the document.",
  },
};

const pt = {
  back: "Voltar",
  save: "Salvar",
  loading: "Carregando…",
  retry: "Tentar de novo",
  nav: {
    overview: "Resumo",
    overview_mobile: "Início",
    protections: "Proteções",
    messages: "Mensagens",
    calls: "Chamadas",
    trusted: "Contatos de confiança",
    sticker: "Sticker Studio",
    membership: "Assinatura e pagamentos",
    settings: "Ajustes",
    logout: "Sair",
    main: "Navegação principal",
    bottom: "Navegação inferior",
    open_menu: "Abrir menu",
    close_menu: "Fechar menu",
    logged_out: "Sessão encerrada",
    tagline: "Contato privado para o que você quer proteger.",
  },
  inbox: {
    title: "Mensagens",
    unread_n: "{n} mensagens não lidas",
    unread_1: "1 mensagem não lida",
    all_clear: "Tudo em ordem",
    search: "Buscar por proteção ou texto",
    filter_all: "Todas",
    filter_unread: "Não lidas",
    filter_urgent: "Urgentes",
    empty_title: "Ainda não há mensagens",
    empty_text: "Quando alguém escanear seu QR e escrever para você, a conversa aparecerá aqui. Você e seus contatos de confiança verão na hora.",
    empty_filter_title: "Nenhuma mensagem com esse filtro",
    empty_filter_text: "Tente outro filtro ou limpe a busca.",
    empty_search_title: "Sem resultados",
    empty_search_text: "Não encontramos conversas que correspondam a “{query}”. Tente outras palavras ou limpe a busca.",
    view_qr: "Ver meus QR",
    select_title: "Selecione uma conversa",
    select_text: "Escolha uma mensagem da lista para ler e responder.",
    select_privacy: "As respostas ficam dentro do AvisoCar: quem escaneia nunca vê seu telefone nem seu e-mail.",
    historic: "Histórico",
    trusted_badge: "Contato de confiança",
    no_messages: "Sem mensagens",
    protection: "Proteção",
    open_state: "Conversa aberta",
    closed_state: "Conversa encerrada",
    urgent: "Aviso urgente",
    block: "Bloquear",
    report: "Denunciar",
    reply_placeholder: "Escreva sua resposta",
    readonly_legacy: "Mensagens históricas: esta conversa é somente leitura.",
    readonly_closed: "Esta conversa está encerrada.",
    not_available: "Esta conversa não está mais disponível.",
    open_error: "Não conseguimos abrir esta conversa",
    send_error: "Não foi possível enviar. Tente de novo",
    send_blocked: "Essa mensagem não pode ser enviada",
    blocked_ok: "Conversa bloqueada",
    reported_ok: "Denúncia enviada à equipe do AvisoCar",
    closed_ok: "Conversa encerrada",
    action_error: "Não foi possível concluir a ação",
    now: "agora",
    min_ago: "há {n} min",
    hour_ago: "há {n} h",
    day_ago: "há {n} d",
  },
  protection: {
    block: "Bloquear conversa",
    unblock: "Desbloquear conversa",
    close: "Encerrar conversa",
    report: "Denunciar abuso",
    reason_label: "Motivo",
    details_label: "Detalhe (opcional)",
    details_ph: "Conte o que aconteceu. Nunca copiamos o conteúdo das mensagens.",
    cancel: "Cancelar",
    confirm_block: "Bloquear conversa",
    confirm_report: "Enviar denúncia",
    reason: {
      harassment: "Assédio ou insistência incômoda",
      threats: "Ameaças",
      spam: "Spam ou publicidade",
      inappropriate: "Conteúdo inadequado",
      not_relevant: "Não tem a ver comigo",
      other: "Outro motivo",
    },
    block_explain: "Bloqueia SOMENTE esta conversa. Quem a iniciou não poderá continuar escrevendo aqui.",
    report_explain: "A denúncia chega à equipe AvisoCar com a referência desta conversa e o seu motivo. Nunca copiamos o texto das mensagens.",
    scope_note: "Seu QR continua ativo, seus contatos de confiança continuam recebendo avisos e qualquer outra pessoa que escanear pode escrever ou ligar normalmente.",
    blocked_badge: "Conversa bloqueada",
    closed_badge: "Conversa encerrada",
    readonly_blocked: "Você bloqueou esta conversa. Pode desbloqueá-la quando quiser.",
    blocked_ok: "Conversa bloqueada",
    unblocked_ok: "Conversa desbloqueada",
    reported_ok: "Denúncia enviada à equipe AvisoCar",
    closed_ok: "Conversa encerrada",
    error: "Não foi possível concluir a ação",
    blocked_title: "Conversas bloqueadas",
    blocked_desc: "Aqui estão as conversas que você bloqueou ou encerrou. O bloqueio é por conversa, não por pessoa: quem escaneia o seu QR é anônimo e não pode ser identificado entre dispositivos.",
    blocked_empty: "Você não bloqueou nenhuma conversa.",
    open_conversation: "Ver",
    rotate_sticker_warning: "Ao trocar o QR, o seu sticker JÁ IMPRESSO deixa de funcionar. Você precisará baixar e imprimir o novo código no Sticker Studio.",
    rotate_sticker_done: "QR trocado. Seu sticker impresso não funciona mais: baixe e imprima o novo código.",
  },
  calls: {
    title: "Chamadas",
    subtitle: "Histórico de chamadas privadas pelo AvisoCar",
    empty_title: "Ainda não há chamadas",
    empty_text: "Quem escanear seu QR pode pedir uma chamada privada pela internet. Ela toca para você e para seus contatos de confiança com permissão de chamadas; quem atender primeiro fica com a chamada.",
    manage_trusted: "Gerenciar contatos de confiança",
    answered_by: "Atendeu",
    view: "Ver",
    state: {
      answered: "Atendida",
      ringing: "Tocando",
      pending: "Solicitada",
      missed: "Perdida",
      declined: "Recusada",
      cancelled: "Cancelada",
      ended: "Finalizada",
      requested: "Solicitada",
    },
    who: { owner: "Dono", trusted: "Contato de confiança", you: "Você" },
  },
  trusted: {
    title: "Contatos de confiança",
    subtitle: "Quem pode te ajudar quando você não puder responder?",
    intro: "Convide uma pessoa de confiança para também receber as mensagens e chamadas dos seus QR. Você decide quais proteções ela pode atender e quais permissões terá.",
    step_l1: "CONVIDAR",
    step_l2: "ACEITAR",
    step_l3: "ATRIBUIR PROTEÇÃO",
    step_l4: "ESCOLHER PERMISSÕES",
    step_l5: "RECEBER AVISOS",
    how_title: "Como funciona",
    step1: "Convide alguém de confiança por e-mail.",
    step2: "Essa pessoa aceita com a própria conta AvisoCar.",
    step3: "Você atribui um ou mais perfis protegidos.",
    step4: "Você escolhe se recebe mensagens, chamadas ou ambos.",
    step5: "Ela pode responder sem compartilhar telefones.",
    invite_title: "Convidar uma pessoa",
    email_label: "E-mail da pessoa",
    name_label: "Nome privado (só você vê)",
    label_label: "Etiqueta pública (opcional)",
    label_hint: "É o único dado que quem escaneia vê. Se deixar vazio aparecerá “Contato de confiança”.",
    submit: "Enviar convite",
    invite_created: "Convite criado. Compartilhe o link com essa pessoa.",
    invite_error: "Não conseguimos criar o convite",
    invite_self: "Você não pode convidar a si mesmo",
    invite_link: "Link do convite (válido por 14 dias):",
    list_title: "Seus contatos",
    show_revoked: "Ver revogados ({n})",
    hide_revoked: "Ocultar revogados",
    empty_title: "Você ainda não convidou ninguém",
    empty_text: "Um contato de confiança pode ler as mensagens e atender as chamadas dos perfis que você atribuir, sem ver seu telefone nem seu e-mail.",
    empty_none_active: "Você não tem contatos ativos nem convites pendentes.",
    load_error: "Não conseguimos carregar seus contatos de confiança",
    status: {
      invited: "Convite enviado",
      accepted: "Aceito · sem perfis",
      assigned: "Ativo",
      declined: "Recusado",
      revoked: "Revogado",
      expired: "Convite expirado",
    },
    status_hint: {
      invited: "Aguardando que aceite com a conta AvisoCar. Ainda não recebe avisos.",
      accepted: "Já aceitou, mas ainda não recebe avisos: atribua pelo menos um perfil protegido.",
      assigned: "Recebe os avisos dos perfis atribuídos conforme as permissões.",
      declined: "Esta pessoa recusou o convite.",
      revoked: "Não recebe mais nenhum aviso dos seus QR.",
      expired: "O link expirou. Envie um convite novo.",
    },
    perm_messages: "Receber mensagens",
    perm_calls: "Receber chamadas",
    perm_none: "Sem permissões ativas: não receberá avisos mesmo com perfis atribuídos.",
    assigned_profiles: "Perfis protegidos atribuídos",
    assigned_count: "{n} perfis atribuídos",
    assigned_none: "Nenhum perfil atribuído",
    no_profiles: "Você ainda não tem perfis protegidos.",
    public_label: "Etiqueta pública",
    public_label_saved: "Etiqueta pública salva",
    public_label_error: "Etiqueta inválida (não pode ser telefone, e-mail nem link)",
    perm_error: "Não conseguimos salvar a permissão",
    profiles_saved: "Perfis atribuídos atualizados",
    profiles_error: "Não conseguimos atualizar os perfis atribuídos",
    revoke: "Revogar",
    revoke_confirm: "Revogar este contato de confiança? Ele deixará de receber avisos.",
    revoked_ok: "Contato revogado",
    revoke_error: "Não conseguimos revogar o contato",
    updated: "Atualizado",
    invited_at: "Convidado",
    accepted_at: "Aceito",
  },
  dash: {
    greeting: "Olá, {name}",
    active_n: "{n} proteções ativas",
    active_1: "1 proteção ativa",
    activate_first: "Ative sua primeira proteção",
    refresh: "Atualizar",
    refreshed: "Painel atualizado",
    stat_qr: "QR ativos",
    stat_unread: "Mensagens não lidas",
    stat_missed: "Chamadas perdidas",
    stat_trusted: "Contatos ativos",
    my_protections: "Minhas proteções",
    new: "Nova",
    empty_title: "Você ainda não tem proteções",
    empty_text: "Crie uma proteção para seu veículo, seu animal, um objeto ou uma criança e receba avisos privados quando alguém escanear o QR.",
    create_first: "Criar minha primeira proteção",
    membership: "Assinatura",
    no_membership: "Sem assinatura ativa",
    plan_months: "{n} meses",
    payment_review: "Pagamento em análise",
    expires_on: "Vence em {date}",
    activate_qr: "Ative para ligar seu QR",
    recent: "Atividade recente",
    see_all: "Ver tudo",
    recent_empty: "Quando alguém escanear seu QR você verá aqui as mensagens e chamadas.",
    trusted_ready: "Contatos de confiança",
    trusted_none: "Ninguém mais recebe seus avisos ainda.",
    trusted_pending: "{n} convite ainda não aceito.",
    trusted_unassigned: "{n} contato aceito sem perfis atribuídos.",
    trusted_ok: "{n} contato recebendo seus avisos.",
    trusted_manage: "Gerenciar",
    view: "Ver",
    sticker: "Sticker",
    pay: "Pagar",
    status: {
      active: "Ativo",
      pending_payment: "Pagamento pendente",
      rejected: "Recusado",
      review: "Em análise",
      pending: "Pendente",
    },
  },
  membership: {
    title: "Assinatura e pagamentos",
    subtitle: "Sua proteção, seu plano e como pagar",
    hero_title: "Proteção ativa para o que importa",
    hero_text: "Sua assinatura mantém os QR das suas proteções ligados: mensagens privadas, chamadas pela internet e avisos aos seus contatos de confiança.",
    status: "Status",
    protected_count: "Proteções ativas",
    expires: "Vence",
    days_left: "Dias restantes",
    days_value: "{n} dias",
    status_active: "Ativa",
    status_expired: "Vencida",
    status_standby: "Em espera",
    status_disabled: "Desativada",
    status_pending: "Pagamento pendente",
    grace: "(em carência)",
    pending_request: "Você tem uma solicitação de pagamento em análise.",
    current_plan: "Seu plano atual",
    choose_plan: "Escolha seu plano",
    recommended: "Recomendado",
    selected: "Selecionado",
    select: "Escolher",
    plan_6m: "6 meses",
    plan_12m: "12 meses",
    per_months: "por {n} meses de proteção",
    benefits_title: "Incluído na sua assinatura",
    benefit_qr: "Contato privado por QR, sem mostrar seu telefone",
    benefit_messages: "Mensagens com quem escaneia, dentro do AvisoCar",
    benefit_calls: "Chamadas privadas pela internet",
    benefit_trusted: "Avisos aos seus contatos de confiança",
    benefit_sticker: "Download de QR e stickers em PNG e PDF",
    benefit_manage: "Gestão de todas as suas proteções em um painel",
    summary_title: "Resumo do pedido",
    summary_plan: "Plano escolhido",
    summary_amount: "Total a pagar",
    summary_method: "Forma de pagamento",
    summary_after: "Depois de pagar",
    summary_after_text: "Analisamos seu pagamento e ligamos seus QR. Avisamos dentro do AvisoCar assim que ficar ativo.",
    charge_note: "Cobrança real: {pen} em soles peruanos (PEN).",
    estimate_note: "Valor aproximado na sua moeda. A cobrança real é feita em soles peruanos (PEN).",
    coupon_title: "Tem um código de desconto?",
    pay_title: "Como você quer pagar",
    pay_none: "No momento não há formas de pagamento disponíveis para o seu país. Fale com a gente que resolvemos.",
    history: "Histórico de pagamentos",
  },
  settings: {
    title: "Ajustes",
    subtitle: "Sua conta, seus avisos e sua privacidade",
  },
  legal: {
    doc_terms: "Termos e Condições",
    doc_privacy: "Política de Privacidade",
    doc_complaints: "Livro de Reclamações (Peru)",
    banner_region: "Atualização legal pendente",
    banner_text: "Atualizamos {docs}. Revise e confirme para continuar usando o AvisoCar normalmente.",
    review: "Revisar",
    dismiss: "Fechar aviso legal",
    close: "Fechar",
    accept: "Aceito",
    later: "Mais tarde",
    accepted_ok: "Obrigado, ficou registrado",
    accept_error: "Não conseguimos registrar sua aceitação",
    load_error: "Não conseguimos carregar o documento.",
  },
};

const fr = {
  back: "Retour",
  save: "Enregistrer",
  loading: "Chargement…",
  retry: "Réessayer",
  nav: {
    overview: "Résumé",
    overview_mobile: "Accueil",
    protections: "Protections",
    messages: "Messages",
    calls: "Appels",
    trusted: "Contacts de confiance",
    sticker: "Sticker Studio",
    membership: "Abonnement et paiements",
    settings: "Réglages",
    logout: "Se déconnecter",
    main: "Navigation principale",
    bottom: "Navigation inférieure",
    open_menu: "Ouvrir le menu",
    close_menu: "Fermer le menu",
    logged_out: "Session fermée",
    tagline: "Contact privé pour ce que vous voulez protéger.",
  },
  inbox: {
    title: "Messages",
    unread_n: "{n} messages non lus",
    unread_1: "1 message non lu",
    all_clear: "Tout est à jour",
    search: "Rechercher par protection ou texte",
    filter_all: "Tous",
    filter_unread: "Non lus",
    filter_urgent: "Urgents",
    empty_title: "Pas encore de messages",
    empty_text: "Quand quelqu'un scanne votre QR et vous écrit, la conversation apparaît ici. Vous et vos contacts de confiance la voyez aussitôt.",
    empty_filter_title: "Aucun message avec ce filtre",
    empty_filter_text: "Essayez un autre filtre ou effacez la recherche.",
    empty_search_title: "Aucun résultat",
    empty_search_text: "Aucune conversation ne correspond à « {query} ». Essayez d'autres mots ou effacez la recherche.",
    view_qr: "Voir mes QR",
    select_title: "Sélectionnez une conversation",
    select_text: "Choisissez un message dans la liste pour le lire et répondre.",
    select_privacy: "Les réponses restent dans AvisoCar : la personne qui scanne ne voit jamais votre téléphone ni votre e-mail.",
    historic: "Archivé",
    trusted_badge: "Contact de confiance",
    no_messages: "Aucun message",
    protection: "Protection",
    open_state: "Conversation ouverte",
    closed_state: "Conversation fermée",
    urgent: "Alerte urgente",
    block: "Bloquer",
    report: "Signaler",
    reply_placeholder: "Écrivez votre réponse",
    readonly_legacy: "Messages archivés : cette conversation est en lecture seule.",
    readonly_closed: "Cette conversation est fermée.",
    not_available: "Cette conversation n'est plus disponible.",
    open_error: "Nous n'avons pas pu ouvrir cette conversation",
    send_error: "Envoi impossible. Réessayez",
    send_blocked: "Ce message ne peut pas être envoyé",
    blocked_ok: "Conversation bloquée",
    reported_ok: "Signalement envoyé à l'équipe AvisoCar",
    closed_ok: "Conversation fermée",
    action_error: "Action impossible",
    now: "maintenant",
    min_ago: "il y a {n} min",
    hour_ago: "il y a {n} h",
    day_ago: "il y a {n} j",
  },
  protection: {
    block: "Bloquer la conversation",
    unblock: "Débloquer la conversation",
    close: "Clore la conversation",
    report: "Signaler un abus",
    reason_label: "Motif",
    details_label: "Détail (facultatif)",
    details_ph: "Dites-nous ce qui s'est passé. Nous ne copions jamais le contenu des messages.",
    cancel: "Annuler",
    confirm_block: "Bloquer la conversation",
    confirm_report: "Envoyer le signalement",
    reason: {
      harassment: "Harcèlement ou insistance gênante",
      threats: "Menaces",
      spam: "Spam ou publicité",
      inappropriate: "Contenu inapproprié",
      not_relevant: "Cela ne me concerne pas",
      other: "Autre motif",
    },
    block_explain: "Cela bloque UNIQUEMENT cette conversation. La personne qui l'a ouverte ne pourra plus vous écrire ici.",
    report_explain: "Le signalement parvient à l'équipe AvisoCar avec la référence de cette conversation et votre motif. Nous ne copions jamais le texte des messages.",
    scope_note: "Votre QR reste actif, vos contacts de confiance continuent de recevoir les avis, et toute autre personne qui scanne peut vous écrire ou vous appeler normalement.",
    blocked_badge: "Conversation bloquée",
    closed_badge: "Conversation close",
    readonly_blocked: "Vous avez bloqué cette conversation. Vous pouvez la débloquer quand vous voulez.",
    blocked_ok: "Conversation bloquée",
    unblocked_ok: "Conversation débloquée",
    reported_ok: "Signalement envoyé à l'équipe AvisoCar",
    closed_ok: "Conversation close",
    error: "Action impossible pour le moment",
    blocked_title: "Conversations bloquées",
    blocked_desc: "Voici les conversations que vous avez bloquées ou closes. Le blocage porte sur une conversation, pas sur une personne : celui qui scanne votre QR est anonyme et ne peut pas être identifié d'un appareil à l'autre.",
    blocked_empty: "Vous n'avez bloqué aucune conversation.",
    open_conversation: "Voir",
    rotate_sticker_warning: "Changer le QR rend votre sticker DÉJÀ IMPRIMÉ inutilisable. Vous devrez télécharger et imprimer le nouveau code depuis Sticker Studio.",
    rotate_sticker_done: "QR changé. Votre sticker imprimé ne fonctionne plus : téléchargez et imprimez le nouveau code.",
  },
  calls: {
    title: "Appels",
    subtitle: "Historique des appels privés via AvisoCar",
    empty_title: "Pas encore d'appels",
    empty_text: "La personne qui scanne votre QR peut demander un appel privé par internet. Il sonne pour vous et pour vos contacts de confiance autorisés ; le premier à répondre prend l'appel.",
    manage_trusted: "Gérer les contacts de confiance",
    answered_by: "Répondu par",
    view: "Voir",
    state: {
      answered: "Répondu",
      ringing: "Sonne",
      pending: "Demandé",
      missed: "Manqué",
      declined: "Refusé",
      cancelled: "Annulé",
      ended: "Terminé",
      requested: "Demandé",
    },
    who: { owner: "Propriétaire", trusted: "Contact de confiance", you: "Vous" },
  },
  trusted: {
    title: "Contacts de confiance",
    subtitle: "Qui peut vous aider quand vous ne pouvez pas répondre ?",
    intro: "Invitez une personne de confiance pour qu'elle reçoive aussi les messages et appels de vos QR. Vous décidez quelles protections elle peut traiter et quelles permissions elle obtient.",
    step_l1: "INVITER",
    step_l2: "ACCEPTER",
    step_l3: "ASSIGNER LA PROTECTION",
    step_l4: "CHOISIR LES PERMISSIONS",
    step_l5: "RECEVOIR LES ALERTES",
    how_title: "Comment ça marche",
    step1: "Invitez une personne de confiance par e-mail.",
    step2: "Elle accepte avec son propre compte AvisoCar.",
    step3: "Vous lui attribuez un ou plusieurs profils protégés.",
    step4: "Vous choisissez si elle reçoit les messages, les appels ou les deux.",
    step5: "Elle peut répondre sans partager de numéro.",
    invite_title: "Inviter une personne",
    email_label: "E-mail de la personne",
    name_label: "Nom privé (vous seul le voyez)",
    label_label: "Étiquette publique (facultatif)",
    label_hint: "C'est la seule information visible par la personne qui scanne. Laissez vide pour afficher « Contact de confiance ».",
    submit: "Envoyer l'invitation",
    invite_created: "Invitation créée. Partagez le lien avec cette personne.",
    invite_error: "Nous n'avons pas pu créer l'invitation",
    invite_self: "Vous ne pouvez pas vous inviter vous-même",
    invite_link: "Lien d'invitation (valable 14 jours) :",
    list_title: "Vos contacts",
    show_revoked: "Voir les révoqués ({n})",
    hide_revoked: "Masquer les révoqués",
    empty_title: "Vous n'avez encore invité personne",
    empty_text: "Un contact de confiance peut lire les messages et prendre les appels des profils que vous lui attribuez, sans voir votre téléphone ni votre e-mail.",
    empty_none_active: "Vous n'avez aucun contact actif ni invitation en attente.",
    load_error: "Nous n'avons pas pu charger vos contacts de confiance",
    status: {
      invited: "Invitation envoyée",
      accepted: "Accepté · sans profil",
      assigned: "Actif",
      declined: "Refusée",
      revoked: "Révoqué",
      expired: "Invitation expirée",
    },
    status_hint: {
      invited: "En attente d'acceptation avec son compte AvisoCar. Elle ne reçoit encore aucune alerte.",
      accepted: "Elle a accepté mais ne reçoit pas encore d'alertes : attribuez au moins un profil protégé.",
      assigned: "Elle reçoit les alertes des profils attribués selon ses autorisations.",
      declined: "Cette personne a refusé l'invitation.",
      revoked: "Elle ne reçoit plus aucune alerte de vos QR.",
      expired: "Le lien a expiré. Envoyez une nouvelle invitation.",
    },
    perm_messages: "Recevoir les messages",
    perm_calls: "Recevoir les appels",
    perm_none: "Aucune autorisation active : elle ne recevra rien même avec des profils attribués.",
    assigned_profiles: "Profils protégés attribués",
    assigned_count: "{n} profils attribués",
    assigned_none: "Aucun profil attribué",
    no_profiles: "Vous n'avez pas encore de profils protégés.",
    public_label: "Étiquette publique",
    public_label_saved: "Étiquette publique enregistrée",
    public_label_error: "Étiquette invalide (ni téléphone, ni e-mail, ni lien)",
    perm_error: "Nous n'avons pas pu enregistrer l'autorisation",
    profiles_saved: "Profils attribués mis à jour",
    profiles_error: "Nous n'avons pas pu mettre à jour les profils attribués",
    revoke: "Révoquer",
    revoke_confirm: "Révoquer ce contact de confiance ? Il ne recevra plus d'alertes.",
    revoked_ok: "Contact révoqué",
    revoke_error: "Nous n'avons pas pu révoquer le contact",
    updated: "Mis à jour",
    invited_at: "Invité",
    accepted_at: "Accepté",
  },
  dash: {
    greeting: "Bonjour, {name}",
    active_n: "{n} protections actives",
    active_1: "1 protection active",
    activate_first: "Activez votre première protection",
    refresh: "Actualiser",
    refreshed: "Tableau de bord actualisé",
    stat_qr: "QR actifs",
    stat_unread: "Messages non lus",
    stat_missed: "Appels manqués",
    stat_trusted: "Contacts actifs",
    my_protections: "Mes protections",
    new: "Nouvelle",
    empty_title: "Vous n'avez pas encore de protection",
    empty_text: "Créez une protection pour votre véhicule, votre animal, un objet ou un enfant et recevez des alertes privées quand quelqu'un scanne son QR.",
    create_first: "Créer ma première protection",
    membership: "Abonnement",
    no_membership: "Aucun abonnement actif",
    plan_months: "{n} mois",
    payment_review: "Paiement en cours d'examen",
    expires_on: "Expire le {date}",
    activate_qr: "Activez-le pour allumer votre QR",
    recent: "Activité récente",
    see_all: "Tout voir",
    recent_empty: "Quand quelqu'un scanne votre QR, vous verrez ici ses messages et appels.",
    trusted_ready: "Contacts de confiance",
    trusted_none: "Personne d'autre ne reçoit encore vos alertes.",
    trusted_pending: "{n} invitation pas encore acceptée.",
    trusted_unassigned: "{n} contact accepté sans profil attribué.",
    trusted_ok: "{n} contact reçoit vos alertes.",
    trusted_manage: "Gérer",
    view: "Voir",
    sticker: "Sticker",
    pay: "Payer",
    status: {
      active: "Actif",
      pending_payment: "Paiement en attente",
      rejected: "Refusé",
      review: "En cours d'examen",
      pending: "En attente",
    },
  },
  membership: {
    title: "Abonnement et paiements",
    subtitle: "Votre protection, votre formule et comment la payer",
    hero_title: "Protection active pour ce qui compte",
    hero_text: "Votre abonnement garde les QR de vos protections allumés : messages privés, appels par internet et alertes à vos contacts de confiance.",
    status: "Statut",
    protected_count: "Protections actives",
    expires: "Expire",
    days_left: "Jours restants",
    days_value: "{n} jours",
    status_active: "Actif",
    status_expired: "Expiré",
    status_standby: "En attente",
    status_disabled: "Désactivé",
    status_pending: "Paiement en attente",
    grace: "(période de grâce)",
    pending_request: "Vous avez une demande de paiement en cours d'examen.",
    current_plan: "Votre formule actuelle",
    choose_plan: "Choisissez votre formule",
    recommended: "Recommandé",
    selected: "Sélectionné",
    select: "Choisir",
    plan_6m: "6 mois",
    plan_12m: "12 mois",
    per_months: "pour {n} mois de protection",
    benefits_title: "Inclus dans votre abonnement",
    benefit_qr: "Contact privé par QR, sans montrer votre téléphone",
    benefit_messages: "Messages avec la personne qui scanne, dans AvisoCar",
    benefit_calls: "Appels privés par internet",
    benefit_trusted: "Alertes à vos contacts de confiance",
    benefit_sticker: "Téléchargement des QR et stickers en PNG et PDF",
    benefit_manage: "Gestion de toutes vos protections depuis un tableau de bord",
    summary_title: "Récapitulatif de la commande",
    summary_plan: "Formule choisie",
    summary_amount: "Total à payer",
    summary_method: "Moyen de paiement",
    summary_after: "Après le paiement",
    summary_after_text: "Nous vérifions votre paiement et allumons vos QR. Nous vous prévenons dans AvisoCar dès que c'est actif.",
    charge_note: "Débit réel : {pen} en soles péruviens (PEN).",
    estimate_note: "Montant approximatif dans votre monnaie. Le débit réel est effectué en soles péruviens (PEN).",
    coupon_title: "Vous avez un code de réduction ?",
    pay_title: "Comment voulez-vous payer",
    pay_none: "Aucun moyen de paiement n'est disponible pour votre pays actuellement. Écrivez-nous et nous réglons ça.",
    history: "Historique des paiements",
  },
  settings: {
    title: "Réglages",
    subtitle: "Votre compte, vos alertes et votre confidentialité",
  },
  legal: {
    doc_terms: "Conditions générales",
    doc_privacy: "Politique de confidentialité",
    doc_complaints: "Livre de réclamations (Pérou)",
    banner_region: "Mise à jour légale en attente",
    banner_text: "Nous avons mis à jour {docs}. Consultez-le et confirmez pour continuer à utiliser AvisoCar normalement.",
    review: "Consulter",
    dismiss: "Fermer l'avis légal",
    close: "Fermer",
    accept: "J'accepte",
    later: "Plus tard",
    accepted_ok: "Merci, c'est enregistré",
    accept_error: "Nous n'avons pas pu enregistrer votre acceptation",
    load_error: "Nous n'avons pas pu charger le document.",
  },
};

import RC6 from "./rcweb6Locales";

export const APP_LOCALES = {
  es: { ...es, ...RC6.es },
  en: { ...en, ...RC6.en },
  pt: { ...pt, ...RC6.pt },
  fr: { ...fr, ...RC6.fr },
};
export default APP_LOCALES;
